/**
 * Profile store — bootstraps the user's central User record + the
 * permission-driven sidebar from `/api/me/profile`.
 *
 * The auth store (`stores/auth.ts`) tracks the multipass session; this
 * store layers on top with the central-schema identity + the resolved
 * nav. Apps consume `useProfile()` to render the sidebar + branching
 * pages without round-tripping permissions on every render.
 */

import { useEffect } from 'react'
import { create } from 'zustand'

import { apiFetch } from '@/api/http'

import { useAuth } from './auth'

export interface ProfileUser {
  id: string
  multipassUserId: string | null
  firstName: string
  lastName: string
  email: string | null
  avatarUri: string | null
  locale: string
  /** Null while the signup funnel is still outstanding — see `needsOnboarding`. */
  onboardingCompletedAt: string | null
}

export type ProfileNavZone = 'priority' | 'work' | 'admin'

export interface ProfileNavItem {
  slug: string
  label: string
  position: number
  zone: ProfileNavZone
  href: string
}

export interface ProfileNavDivider {
  afterPosition: number
}

/**
 * Plan 25 — kiosk session context surfaced by the bootstrap route.
 * `paired === true` when the inbound request authenticated via a
 * kiosk bearer token; the CafeteriaLauncher reads this to decide
 * whether to render its fixed-bottom button.
 */
export interface KioskContext {
  paired: boolean
  sessionId: string | null
  locationId: string | null
}

/**
 * PROD-1168 — the server's verdict for the cafeteria launcher. `enabled`
 * mirrors `hasCafeteriaAccess` in `server/src/utils/cafeteria-auth.ts`
 * (school staff by profile OR an effective chef cafeteria grant issued by
 * the Ordo team); `grantOnly` = the access comes only from a grant, which
 * switches the LocationLockGate empty state to the chef wording. Never
 * re-derive this from `profileCodes` — they cannot see grants.
 */
export interface CafeteriaAccess {
  enabled: boolean
  grantOnly: boolean
}

export interface Profile {
  portalPersona?: 'supplier' | 'chef' | null
  user: ProfileUser | null
  /** Server's verdict — see `needsOnboarding` below. */
  needsOnboarding: boolean
  /**
   * Ordo staff session (INTERNAL + global admin or Customers ADMIN), decided by
   * the server. The "Ordo Admin" panopticon branches read THIS; `profileCodes`
   * never carries the retired ORDO_ADMIN code.
   */
  isOrdoStaff: boolean
  profileCodes: string[]
  appRoles: Record<string, 'NONE' | 'VIEWER' | 'EDITOR' | 'ADMIN'>
  featureFlags: Record<string, unknown>
  permissions: string[]
  nav: {
    items: ProfileNavItem[]
    dividers: ProfileNavDivider[]
  }
  kioskContext: KioskContext
  cafeteriaAccess: CafeteriaAccess
}

/**
 * True when this session still owes the signup funnel — today, an account
 * imported from the legacy ordo app, which carries a multipass identity and
 * the old password but no school, roles, phone or student claims.
 *
 * The verdict is the SERVER's (`server/src/managers/onboarding.ts`), not a
 * client re-derivation. A null `onboardingCompletedAt` alone is not
 * sufficient: staff arriving through Google, and invitees provisioned by an
 * invitation rather than the funnel, also carry one, and funneling them would
 * drag a fully-provisioned user back through a customer signup. This wrapper
 * exists only to keep the loading case (`profile === null`) from redirecting
 * on state we haven't read yet.
 */
export function needsOnboarding(profile: Profile | null): boolean {
  return profile?.needsOnboarding === true
}

interface ProfileState {
  profile: Profile | null
  status: 'idle' | 'loading' | 'error'
  refresh: () => Promise<void>
  reset: () => void
}

export const useProfileStore = create<ProfileState>((set) => ({
  profile: null,
  status: 'idle',
  refresh: async () => {
    set({ status: 'loading' })
    try {
      // Route through apiFetch so the per-tab impersonation headers are sent —
      // otherwise /me/profile resolves the operator's own session (wrong nav +
      // profileCodes), not the impersonated org.
      const body = await apiFetch<Partial<Profile>>('/api/me/profile')
      // The server returns user=null when multipass session is valid
      // but no central User row exists yet (onboarding pending).
      // Default kioskContext defensively so pre-Plan-25 responses
      // (no `kioskContext` field) don't break the launcher visibility check.
      const profile: Profile = {
        user: body.user ?? null,
        // Defaults FALSE: an older server that doesn't send the field must
        // never push a user into the signup funnel.
        needsOnboarding: body.needsOnboarding ?? false,
        portalPersona: body.portalPersona ?? null,
        // Defaults FALSE: staff power is never assumed from an older server.
        isOrdoStaff: body.isOrdoStaff === true,
        profileCodes: body.profileCodes ?? [],
        appRoles: body.appRoles ?? {},
        featureFlags: body.featureFlags ?? {},
        permissions: body.permissions ?? [],
        nav: body.nav ?? { items: [], dividers: [] },
        kioskContext: body.kioskContext ?? {
          paired: false,
          sessionId: null,
          locationId: null,
        },
        // Defaults DISABLED: an older server that doesn't send the field must
        // not hide the launcher from school staff — the launcher predicate
        // keeps its profile-code fallback for exactly that deploy-skew window.
        cafeteriaAccess: body.cafeteriaAccess ?? { enabled: false, grantOnly: false },
      }
      set({ profile, status: 'idle' })
    } catch {
      set({ profile: null, status: 'error' })
    }
  },
  reset: () => set({ profile: null, status: 'idle' }),
}))

/**
 * Hook: returns the profile + a `loading` flag. Auto-fetches once the
 * multipass session is established, clears when the user signs out.
 */
export function useProfile() {
  const { user: authUser } = useAuth()
  const state = useProfileStore()

  useEffect(() => {
    if (!authUser) {
      // signed out → drop the profile
      if (state.profile !== null) {
        state.reset()
      }
      return
    }
    // signed in but no profile yet (first load OR after sign-out)
    if (!state.profile && state.status !== 'loading') {
      void state.refresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id])

  return {
    profile: state.profile,
    loading: state.status === 'loading',
    error: state.status === 'error',
    refresh: state.refresh,
  }
}
